import { useState, useMemo, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { LayoutDashboard, FileText, Receipt, Wallet, Users, Settings, LogOut, Database, AlertTriangle, Smartphone, Landmark, FileSpreadsheet, BarChart3, Menu, X, PieChart, ChevronLeft, ChevronRight, ChevronDown, Sparkles, Mail, ListTodo, Gavel, Workflow, Wallet2, Inbox, Navigation, IndianRupee } from "lucide-react";
import { useAuth } from "../lib/auth";
import { canAccess } from "../lib/pages";
import { GarvWidget } from "./GarvWidget";
import { TabBar } from "./TabBar";
import { NotificationBell } from "./NotificationBell";
import { ThemeToggle } from "./ThemeToggle";

const items = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", key: "dashboard" },
  { to: "/lrs", label: "Lorry Receipts", icon: FileText, testId: "nav-lrs", key: "lrs" },
  { to: "/consignments", label: "Consignments", icon: Workflow, testId: "nav-consignments", key: "consignments" },
  { to: "/field", label: "Field Mode (POD)", icon: Smartphone, testId: "nav-field", key: "field" },
  { to: "/live-tracking", label: "Live Tracking", icon: Navigation, testId: "nav-live-tracking", key: "live_tracking" },
  { to: "/quotations", label: "Quotations", icon: FileSpreadsheet, testId: "nav-quotations", key: "quotations" },
  { to: "/leads", label: "Website Leads", icon: Inbox, testId: "nav-leads", key: "leads" },
  { to: "/bids", label: "L&T Bids", icon: Gavel, testId: "nav-bids", key: "bids" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testId: "nav-invoices", key: "invoices" },
  { to: "/aging", label: "Aging Report", icon: AlertTriangle, testId: "nav-aging", key: "aging" },
  { to: "/expenses", label: "Expenses", icon: Wallet, testId: "nav-expenses", key: "expenses" },
  { to: "/bank-statements", label: "Bank Statements", icon: Landmark, testId: "nav-bank-statements", key: "bank-statements" },
  { to: "/personal-accounts", label: "Personal Accounts", icon: Wallet2, testId: "nav-personal-accounts", key: "personal_accounts" },
  { to: "/payments", label: "UPI Payments", icon: IndianRupee, testId: "nav-payments", key: "payments" },
  { to: "/payroll", label: "Payroll & Office", icon: Wallet2, testId: "nav-payroll", key: "payroll" },
  { to: "/reports", label: "Reports", icon: BarChart3, testId: "nav-reports", key: "reports" },
  { to: "/summary", label: "Summary", icon: PieChart, testId: "nav-summary", key: "summary" },
  { to: "/assistant", label: "AI Assistant", icon: Sparkles, testId: "nav-assistant", key: "assistant" },
  { to: "/inbox", label: "Inbox Assistant", icon: Mail, testId: "nav-inbox", key: "inbox" },
  { to: "/reminders", label: "Reminders", icon: ListTodo, testId: "nav-reminders", key: "reminders" },
  { to: "/masters", label: "Vehicles & Drivers", icon: Database, testId: "nav-masters", key: "masters" },
  { to: "/users", label: "Users", icon: Users, testId: "nav-users", key: "users", admin: true },
  { to: "/settings", label: "Settings", icon: Settings, testId: "nav-settings", key: "settings", admin: true },
];

const GROUPS = [
  { id: "operations", label: "Operations", keys: ["dashboard", "lrs", "consignments", "field", "live_tracking"] },
  { id: "sales", label: "Sales", keys: ["quotations", "leads", "bids"] },
  { id: "finance", label: "Billing & Finance", keys: ["invoices", "aging", "expenses", "bank-statements", "personal_accounts", "payments", "payroll"] },
  { id: "insights", label: "Insights", keys: ["reports", "summary"] },
  { id: "assistant", label: "Assistant", keys: ["assistant", "inbox", "reminders"] },
  { id: "admin", label: "Administration", keys: ["masters", "users", "settings"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "1");

  async function handleLogout() {
    await logout();
    navigate("/login");
  }
  const close = () => setOpen(false);
  const toggleCollapse = () => setCollapsed((c) => { localStorage.setItem("sidebar_collapsed", c ? "0" : "1"); return !c; });
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sidebar_groups") || "[]"); } catch { return []; }
  });
  const toggleGroup = (id) => setCollapsedGroups((g) => {
    const next = g.includes(id) ? g.filter((x) => x !== id) : [...g, id];
    localStorage.setItem("sidebar_groups", JSON.stringify(next));
    return next;
  });
  const navItems = useMemo(() => items.filter((it) => (it.admin ? user?.role === "admin" : canAccess(user, it.key))), [user]);

  // Poll for new company mail so the Inbox nav shows a "N new" badge even when
  // the user is on another screen. Cleared the moment they acknowledge on the Inbox.
  const [inboxNew, setInboxNew] = useState(0);
  useEffect(() => {
    if (!canAccess(user, "inbox")) return;
    let active = true;
    async function check() {
      try {
        const since = Number(localStorage.getItem("inbox_last_seen_ts") || 0);
        const { data } = await api.get(`/gmail/new-count?since=${since}`);
        if (!active) return;
        if (!since && data.latest_ts) {
          localStorage.setItem("inbox_last_seen_ts", String(data.latest_ts));
          setInboxNew(0);
        } else {
          setInboxNew(data.new_count || 0);
        }
      } catch { /* ignore (not connected / offline) */ }
    }
    check();
    const id = setInterval(check, 30000);
    const onSeen = () => setInboxNew(0);
    window.addEventListener("inbox-seen", onSeen);
    return () => { active = false; clearInterval(id); window.removeEventListener("inbox-seen", onSeen); };
  }, [user]);

  // Poll auto-filed directory entries so the Masters nav shows how many document-
  // parsed contacts/addresses are waiting for review.
  const [autoFiledCount, setAutoFiledCount] = useState(0);
  useEffect(() => {
    if (!canAccess(user, "masters")) return undefined;
    let active = true;
    async function check() {
      try {
        const { data } = await api.get("/directory/auto-filed/count");
        if (active) setAutoFiledCount(data.count || 0);
      } catch { /* ignore */ }
    }
    check();
    const id = setInterval(check, 30000);
    const onReviewed = () => check();
    window.addEventListener("auto-filed-reviewed", onReviewed);
    return () => { active = false; clearInterval(id); window.removeEventListener("auto-filed-reviewed", onReviewed); };
  }, [user]);

  // Poll new website leads so the Leads nav shows a count badge.
  const [leadsNew, setLeadsNew] = useState(0);
  useEffect(() => {
    if (!canAccess(user, "leads")) return undefined;
    let active = true;
    async function check() {
      try {
        const { data } = await api.get("/leads/count");
        if (active) setLeadsNew(data.new || 0);
      } catch { /* ignore */ }
    }
    check();
    const id = setInterval(check, 30000);
    const onSeen = () => check();
    window.addEventListener("leads-changed", onSeen);
    return () => { active = false; clearInterval(id); window.removeEventListener("leads-changed", onSeen); };
  }, [user]);

  const linkClass = ({ isActive }) =>
    `relative flex items-center gap-3 py-3 text-sm transition-colors ${collapsed ? "md:justify-center md:px-0 px-6" : "px-6"} ` +
    (isActive
      ? "bg-[#E5E3DB] text-[#1A1A18] border-r-2 border-[#C84B31] font-medium"
      : "text-[#68665E] hover:bg-[#E5E3DB]/50 hover:text-[#1A1A18]");

  const renderItem = (it) => (
    <NavLink key={it.to} to={it.to} end={it.to === "/app"} data-testid={it.testId} onClick={close} className={linkClass} title={it.label}>
      <it.icon className="w-4 h-4 shrink-0" />
      <span className={collapsed ? "md:hidden" : ""}>{it.label}</span>
      {it.key === "inbox" && inboxNew > 0 && (
        <>
          <span data-testid="nav-inbox-badge"
            className={`ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#DA1E28] text-white text-[10px] font-semibold ${collapsed ? "md:hidden" : ""}`}>
            {inboxNew > 99 ? "99+" : inboxNew}
          </span>
          {collapsed && <span data-testid="nav-inbox-dot" className="hidden md:block absolute top-2 right-2.5 w-2 h-2 rounded-full bg-[#DA1E28]" />}
        </>
      )}
      {it.key === "leads" && leadsNew > 0 && (
        <>
          <span data-testid="nav-leads-badge"
            className={`ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#C84B31] text-white text-[10px] font-semibold ${collapsed ? "md:hidden" : ""}`}>
            {leadsNew > 99 ? "99+" : leadsNew}
          </span>
          {collapsed && <span data-testid="nav-leads-dot" className="hidden md:block absolute top-2 right-2.5 w-2 h-2 rounded-full bg-[#C84B31]" />}
        </>
      )}
      {it.key === "masters" && autoFiledCount > 0 && (
        <>
          <span data-testid="nav-masters-badge"
            className={`ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#2B5FB3] text-white text-[10px] font-semibold ${collapsed ? "md:hidden" : ""}`}>
            {autoFiledCount > 99 ? "99+" : autoFiledCount}
          </span>
          {collapsed && <span data-testid="nav-masters-dot" className="hidden md:block absolute top-2 right-2.5 w-2 h-2 rounded-full bg-[#2B5FB3]" />}
        </>
      )}
    </NavLink>
  );

  return (
    <div className="min-h-screen flex bg-[#FDFCF8]">
      {open && <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={close} data-testid="sidebar-overlay" />}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 ${collapsed ? "md:w-16" : "md:w-64"} bg-[#F5F4F0] border-r border-[#E5E3DB] flex flex-col transform transition-all duration-200 md:static md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="app-sidebar"
      >
        <div className={`py-6 border-b border-[#E5E3DB] flex items-center gap-3 ${collapsed ? "md:px-3 px-6" : "px-6"}`}>
          <img src="/ito-logo.png" alt="ITO" className="w-11 h-11 object-contain shrink-0" data-testid="brand-logo" />
          <div className={`flex-1 ${collapsed ? "md:hidden" : ""}`}>
            <div className="font-headings font-bold text-[#1A1A18] leading-tight text-sm">INDIAN TRANSPORT</div>
            <div className="font-headings font-bold text-[#C84B31] leading-tight text-sm">ORGANISATION</div>
            <div className="text-[9px] uppercase tracking-widest text-[#68665E] mt-1">Logistics OS</div>
          </div>
          <button className="md:hidden text-[#68665E]" onClick={close} data-testid="sidebar-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Desktop collapse toggle */}
        <button onClick={toggleCollapse} data-testid="sidebar-collapse-toggle" title={collapsed ? "Expand" : "Collapse"}
          className="hidden md:flex items-center justify-center py-2 border-b border-[#E5E3DB] text-[#68665E] hover:text-[#C84B31] hover:bg-[#E5E3DB]/40 transition-colors">
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <><ChevronLeft className="w-4 h-4" /> <span className="text-xs ml-1">Collapse</span></>}
        </button>

        <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
          {GROUPS.map((g) => {
            const groupItems = g.keys.map((k) => navItems.find((n) => n.key === k)).filter(Boolean);
            if (groupItems.length === 0) return null;
            const groupCollapsed = collapsedGroups.includes(g.id);
            return (
              <div key={g.id} className="mb-1">
                {!collapsed ? (
                  <button onClick={() => toggleGroup(g.id)} data-testid={`nav-group-${g.id}`}
                    className="w-full flex items-center justify-between px-6 py-2 text-[10px] font-semibold uppercase tracking-widest text-[#A09E96] hover:text-[#68665E] transition-colors">
                    <span>{g.label}</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${groupCollapsed ? "-rotate-90" : ""}`} />
                  </button>
                ) : (
                  <div className="mx-3 my-1.5 border-t border-[#E5E3DB]" />
                )}
                {(collapsed || !groupCollapsed) && groupItems.map((it) => renderItem(it))}
              </div>
            );
          })}
        </nav>

        <div className={`border-t border-[#E5E3DB] py-4 ${collapsed ? "md:px-2 px-6" : "px-6"}`}>
          <div className={`${collapsed ? "md:hidden" : ""}`}>
            <div className="text-sm font-medium text-[#1A1A18]" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs text-[#68665E] uppercase tracking-wide">{user?.role}</div>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden min-w-0">
        <header className="sticky top-0 z-30 flex items-center gap-3 bg-[#F5F4F0] border-b border-[#E5E3DB] px-4 py-3">
          <button onClick={() => setOpen(true)} data-testid="sidebar-toggle" className="md:hidden text-[#1A1A18]"><Menu className="w-6 h-6" /></button>
          <img src="/ito-logo.png" alt="ITO" className="w-8 h-8 object-contain md:hidden" />
          <span className="font-headings font-bold text-[#1A1A18] text-sm md:hidden">ITO Logistics</span>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-right leading-tight hidden sm:block">
              <div className="text-sm font-medium text-[#1A1A18]" data-testid="header-user-name">{user?.name}</div>
              <div className="text-[10px] text-[#68665E] uppercase tracking-wide">{user?.role}</div>
            </div>
            <ThemeToggle testId="theme-toggle" />
            <NotificationBell />
            <button onClick={handleLogout} className="btn-ghost text-sm" data-testid="logout-button" title="Sign out">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </header>
        <TabBar />
        <div className="p-4 sm:p-6 md:p-10 fadeup">{children}</div>
      </main>
      <GarvWidget />
    </div>
  );
}

